import { IDropdownOption, Stack, Text, Dropdown, DatePicker, DayOfWeek, ITextFieldProps, TextField as Field, IDatePickerProps } from '@fluentui/react';
import { useDebouncedEventHandler } from '@pum-spark/react-hooks';
import { useMutation } from '@tanstack/react-query';
import React, { useRef, useMemo } from 'react';
import { ApiCalls } from '../../../../../api/api';
import { TimeUser, Calendar, SetUserAdministrationDto } from '../../../../../api/generated/data-contracts';
import { useCalendars } from '../../../../../hooks/queries/useCalendars';
import { useConfigValuesState } from '../../../../../hooks/useConfigValue';
import { FunctionOr } from '../../../../../types/FunctionOr';

export const BasicUserInfo = ({ user, setUser }: { user: TimeUser; setUser: (update: FunctionOr<TimeUser>) => void }) => {
    const {
        initialData: {
            configuration: { capacityDefault },
            aggregatedUser: { originalUser },
        },
        setConfigValueCache,
    } = useConfigValuesState();
    const defaultCalendars = useRef([] as Calendar[]).current;
    const { data: allCalendars = defaultCalendars } = useCalendars({ suspense: false });
    const { mutate } = useMutation({
        mutationFn: (props: Partial<SetUserAdministrationDto>) => {
            return ApiCalls.setBasicAdministrativeUserProperties({ ...props, userId: user.id });
        },
        onMutate: ({ calendarId, end, isNotActive, start, availability }) => {
            const userCalendarId = calendarId ?? user.calendar?.id ?? null;
            const calendar = allCalendars.find(calendar => calendar.id === userCalendarId);
            const updatedValues = {
                end: end ?? user.end ?? null,
                start: start ?? user.start ?? null,
                isNotActive: isNotActive ?? user.isNotActive ?? null,
                availability: availability ?? null,
                calendar,
            };
            setUser(user => ({
                ...user,
                ...updatedValues,
            }));
            if (user.id === originalUser.id) {
                setConfigValueCache(config => ({
                    ...config,
                    aggregatedUser: {
                        ...config.aggregatedUser,
                        originalUser: {
                            ...config.aggregatedUser.originalUser,
                            ...updatedValues,
                        },
                        user: {
                            ...config.aggregatedUser.user,
                            ...updatedValues,
                        },
                    },
                }));
            }
        },
    });

    const startDate = useMemo(() => new Date(user.start), [user.start]);
    const endDate = useMemo(() => new Date(user.end), [user.end]);

    const calendarOptions = useMemo(
        () =>
            allCalendars.map(
                calendar =>
                    ({
                        key: calendar.id,
                        text: calendar.name,
                        data: calendar,
                    } as IDropdownOption<Calendar>),
            ),
        [allCalendars],
    );

    const stateOptions = useMemo<IDropdownOption[]>(
        () => [
            { key: 'active', text: 'Active' },
            { key: 'inactive', text: 'Inactive' },
        ],
        [],
    );

    const capacity = useMemo(() => {
        return String(user.availability || '');
    }, [user.availability]);

    const capacityInfo = useMemo(() => {
        if (user.availability === null) {
            if (user.calendar?.dailyCapacity) {
                return `Current calendar capacity (${user.calendar?.dailyCapacity} hours)`;
            }
            return `Current default capacity (${capacityDefault} hours)`;
        }
    }, [capacityDefault, user.availability, user.calendar?.dailyCapacity]);

    const handleCapacityChange = useDebouncedEventHandler((e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, val?: string) => {
        mutate({ availability: Number(val) || null });
    });

    return (
        <Stack tokens={{ childrenGap: 20 }} horizontal>
            <Stack styles={{ root: { width: '100%' } }}>
                <Stack>
                    <Text styles={{ root: { fontWeight: 600 } }}>
                        Resource information
                        <hr />
                    </Text>
                    <TextField readOnly label="Name" disabled value={user.name} />
                    <TextField readOnly label="E-mail" disabled value={user.email} />
                    <TextField readOnly label="Job Title" disabled value={user.jobTitle} />
                    <TextField readOnly label="Office Location" disabled value={user.officeLocation} />
                    <TextField readOnly label="User Number" disabled value={user.userNumber} />
                </Stack>
            </Stack>
            <Stack styles={{ root: { width: '100%' } }}>
                <Text styles={{ root: { fontWeight: 600 } }}>
                    Availability
                    <hr />
                </Text>
                <Dropdown
                    label="State"
                    options={stateOptions}
                    defaultSelectedKey={user.isNotActive ? 'inactive' : 'active'}
                    onChange={(e, option) => {
                        mutate({ isNotActive: option.key === 'inactive' });
                    }}
                    disabled={originalUser.id === user.id}
                />
                <TextField label="Daily Capacity (hours)" type="number" value={capacity} onChange={handleCapacityChange} placeholder={capacityInfo} />
                <Dropdown
                    label="Calendar"
                    options={calendarOptions}
                    selectedKey={user.calendar?.id}
                    onChange={(e, option) => {
                        mutate({ calendarId: option.key as string });
                    }}
                />
                <DatePicker
                    label="Earliest Available"
                    value={startDate}
                    firstDayOfWeek={DayOfWeek.Monday}
                    onSelectDate={start => {
                        mutate({ start: start.getISOStringMidnight() });
                    }}
                    placeholder="Select a date..."
                    ariaLabel="Select a date"
                    styles={datePickerStyles}
                />
                <DatePicker
                    label="Latest Available"
                    value={endDate}
                    firstDayOfWeek={DayOfWeek.Monday}
                    onSelectDate={end => {
                        mutate({ end: end.getISOStringMidnight() });
                    }}
                    placeholder="Select a date..."
                    ariaLabel="Select a date"
                    styles={datePickerStyles}
                />
            </Stack>
        </Stack>
    );
};
const TextField = ({ value, ...rest }: ITextFieldProps) => <Field value={value ?? ''} {...rest} />;

const datePickerStyles: IDatePickerProps['styles'] = { root: { height: '60.5px' } };
